<template>
  <v-dialog v-model="showModal" max-width="800px" persistent>
    <v-card>
      <v-card-title class="modal-header">
        <span>Bulk Edit Requests ({{ itemCount }} items)</span>
      </v-card-title>

      <v-card-text class="pt-4">
        <v-alert type="info" variant="tonal" class="mb-4">
          Only fill in the fields you want to update. Empty fields will be left unchanged.
        </v-alert>

        <v-alert v-if="error" type="error" variant="tonal" class="mb-4">
          {{ error }}
        </v-alert>

        <v-form ref="formRef">
          <v-row>
            <v-col cols="12" sm="6">
              <v-select
                v-model="localFormData.status"
                :items="statusOptions"
                label="Status (optional)"
                variant="outlined"
                clearable
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="localFormData.school"
                :items="schoolOptions"
                label="School (optional)"
                variant="outlined"
                clearable
              ></v-select>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="6">
              <v-select
                v-model="localFormData.equipment_type"
                :items="equipmentTypeOptions"
                label="Equipment Type (optional)"
                variant="outlined"
                clearable
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="localFormData.minimum_rating"
                label="Minimum Rating 1-10 (optional)"
                type="number"
                min="1"
                max="10"
                variant="outlined"
                clearable
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="6">
              <v-select
                v-model="localFormData.grade_level"
                :items="gradeLevelOptions"
                label="Grade Level (optional)"
                variant="outlined"
                clearable
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="localFormData.assignee"
                label="Assignee (optional)"
                variant="outlined"
                clearable
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-textarea
                v-model="localFormData.notes"
                label="Notes (optional)"
                variant="outlined"
                rows="3"
                placeholder="Add notes or comments..."
                clearable
              ></v-textarea>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="grey"
          variant="text"
          @click="$emit('close')"
          :disabled="submitting"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          @click="handleSubmit"
          :loading="submitting"
        >
          Apply Changes
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
/* eslint-disable no-undef */
import { ref, watch } from 'vue'

interface BulkEditFormData {
  status?: string
  school?: string
  equipment_type?: string
  minimum_rating?: number | null
  grade_level?: string
  assignee?: string
  notes?: string
}

interface Props {
  show: boolean
  itemCount: number
  submitting?: boolean
  error?: string
  schools?: any[]
}

const props = withDefaults(defineProps<Props>(), {
  show: false,
  itemCount: 0,
  submitting: false,
  error: '',
  schools: () => []
})

const emit = defineEmits<{
  'close': []
  'submit': [data: BulkEditFormData]
}>()

const showModal = ref(props.show)
const formRef = ref<any>(null)

const localFormData = ref<BulkEditFormData>({
  status: undefined,
  school: undefined,
  equipment_type: undefined,
  minimum_rating: null,
  grade_level: undefined,
  assignee: undefined,
  notes: undefined
})

const statusOptions = [
  { title: 'Pending', value: 'pending' },
  { title: 'Filled', value: 'filled' },
  { title: 'Completed', value: 'completed' },
  { title: 'Denied', value: 'denied' }
]

const equipmentTypeOptions = [
  { title: 'Laptop', value: 'laptop' },
  { title: 'Desktop', value: 'desktop' },
  { title: 'Monitor', value: 'monitor' },
  { title: 'Tablet', value: 'tablet' },
  { title: 'Other', value: 'other' }
]

const gradeLevelOptions = [
  '6', '7', '8', '9', '10', '11', '12'
]

const schoolOptions = ref<any[]>([])

watch(() => props.schools, (newSchools) => {
  schoolOptions.value = newSchools.map((s: any) => ({
    title: s.name,
    value: s.id || s._id
  }))
}, { immediate: true })

watch(() => props.show, (newVal) => {
  showModal.value = newVal
  if (newVal) {
    // Reset form when modal opens
    localFormData.value = {
      status: undefined,
      school: undefined,
      equipment_type: undefined,
      minimum_rating: null,
      grade_level: undefined,
      assignee: undefined,
      notes: undefined
    }
  }
})

watch(showModal, (newVal) => {
  if (!newVal) {
    emit('close')
  }
})

const handleSubmit = async () => {
  // Filter out undefined/null values - only send fields that user filled in
  const updateData: BulkEditFormData = {}

  if (localFormData.value.status !== undefined && localFormData.value.status !== null) {
    updateData.status = localFormData.value.status
  }
  if (localFormData.value.school !== undefined && localFormData.value.school !== null && localFormData.value.school !== '') {
    updateData.school = localFormData.value.school
  }
  if (localFormData.value.equipment_type !== undefined && localFormData.value.equipment_type !== null) {
    updateData.equipment_type = localFormData.value.equipment_type
  }
  if (localFormData.value.minimum_rating !== undefined && localFormData.value.minimum_rating !== null) {
    updateData.minimum_rating = localFormData.value.minimum_rating
  }
  if (localFormData.value.grade_level !== undefined && localFormData.value.grade_level !== null) {
    updateData.grade_level = localFormData.value.grade_level
  }
  if (localFormData.value.assignee !== undefined && localFormData.value.assignee !== null && localFormData.value.assignee !== '') {
    updateData.assignee = localFormData.value.assignee
  }
  if (localFormData.value.notes !== undefined && localFormData.value.notes !== null && localFormData.value.notes !== '') {
    updateData.notes = localFormData.value.notes
  }

  emit('submit', updateData)
}
</script>

<style scoped>
.modal-header {
  background: linear-gradient(135deg, var(--primary-color, #2c4a6b) 0%, var(--secondary-color, #3ba3d1) 100%);
  color: var(--white, #ffffff);
  font-weight: 600;
  font-size: 1.25rem;
}
</style>
